import { signal } from '@preact/signals-react';
import type { ConversationMessage } from '@/embedded/chat/types/conversation-message';
import {
  HTTPStatusError,
  isAgentDBMigrationTransientError,
  type WebAgentConversationEvent,
  type WebAgentConversationInfo,
  type WebAgentConversationMessage,
  type WebAgentConversationMessageDraft,
  type WebConversationListItem,
} from '@/website/lib/chat-api';
import {
  isListedConversationKind,
  normalizeText,
  uniqueNormalizedIds,
} from './conversation-utils';
import type { ConversationCoreTransport } from './types';

type StreamOpenInfo = {
  isReconnect: boolean;
};

type ConversationMutationSource = 'snapshot' | 'live' | 'local';

type ConversationUpsertOptions = {
  restoreDeleted?: boolean;
  mutationSource?: ConversationMutationSource;
  snapshotStartSequence?: number;
};

function agentDBRetryDelayMs(error: unknown): number | null {
  if (!isAgentDBMigrationTransientError(error)) return null;
  const retryAfterSeconds =
    error instanceof HTTPStatusError ? error.retryAfterSeconds : null;
  return Math.max(1000, (retryAfterSeconds ?? 5) * 1000);
}

function isConversationInfo(
  value: WebAgentConversationInfo | WebConversationListItem
): value is WebAgentConversationInfo {
  return 'kind' in value;
}

function isListedConversationInfo(conversation: WebAgentConversationInfo) {
  return isListedConversationKind(conversation.kind);
}

function appendMessage(
  messages: WebAgentConversationMessage[],
  message: WebAgentConversationMessage
) {
  const existingIndex = messages.findIndex(
    current => current.messageId === message.messageId
  );
  if (existingIndex === -1) return [...messages, message];
  const next = [...messages];
  next[existingIndex] = message;
  return next;
}

function messageIdOf(
  message: WebAgentConversationMessage | ConversationMessage
) {
  return normalizeText(message.messageId);
}

function draftTimestamp(draft: WebAgentConversationMessageDraft) {
  return draft.updatedAt ?? draft.createdAt ?? Number.NEGATIVE_INFINITY;
}

function isWebAgentConversationMessage(
  message: WebAgentConversationMessage | ConversationMessage
): message is WebAgentConversationMessage {
  return (
    typeof (message as WebAgentConversationMessage).participantId ===
      'string' &&
    typeof (message as WebAgentConversationMessage).createdAt === 'number'
  );
}

function upsertDraft(
  drafts: WebAgentConversationMessageDraft[],
  draft: WebAgentConversationMessageDraft
) {
  if (draft.status === 'cancelled') {
    return drafts.filter(current => current.draftId !== draft.draftId);
  }
  if (draft.status === 'completed') {
    return drafts;
  }

  const existingIndex = drafts.findIndex(
    current => current.draftId === draft.draftId
  );
  if (existingIndex === -1) return [...drafts, draft];
  const next = [...drafts];
  next[existingIndex] = draft;
  return next;
}

function removeLatestDraftForParticipant(
  drafts: WebAgentConversationMessageDraft[],
  participantId: string | null | undefined
) {
  const normalizedParticipantId = normalizeText(participantId);
  if (!normalizedParticipantId) return drafts;

  const draftIndex = drafts.findLastIndex(
    draft => normalizeText(draft.participantId) === normalizedParticipantId
  );
  if (draftIndex === -1) return drafts;

  return drafts.filter((_, index) => index !== draftIndex);
}

function removeStaleDraftsForMessages(
  drafts: WebAgentConversationMessageDraft[],
  messages: Array<WebAgentConversationMessage | ConversationMessage>
) {
  if (drafts.length === 0) return drafts;

  const persistedMessages = messages
    .filter(isWebAgentConversationMessage)
    .flatMap(message => {
      const participantId = normalizeText(message.participantId);
      if (!participantId || normalizeText(message.kind) !== 'message') {
        return [];
      }
      return [{ participantId, createdAt: message.createdAt }];
    });
  if (persistedMessages.length === 0) return drafts;

  return drafts.filter(draft => {
    const participantId = normalizeText(draft.participantId);
    if (!participantId) return true;
    const timestamp = draftTimestamp(draft);
    return !persistedMessages.some(
      message =>
        message.participantId === participantId &&
        message.createdAt >= timestamp
    );
  });
}

export class ConversationLiveDataset {
  readonly agentGroupId = signal<string | null>(null);
  readonly conversationInfoById = signal(
    new Map<string, WebAgentConversationInfo>()
  );
  readonly listedConversationIds = signal<string[]>([]);
  readonly deletedConversationIds = signal(new Set<string>());
  readonly messagesByConversationId = signal(
    new Map<string, Array<WebAgentConversationMessage | ConversationMessage>>()
  );
  readonly createdMessagesByConversationId = signal(
    new Map<string, WebAgentConversationMessage[]>()
  );
  readonly draftsByConversationId = signal(
    new Map<string, WebAgentConversationMessageDraft[]>()
  );
  readonly conversationListRefreshing = signal(false);
  readonly conversationListHydrated = signal(false);
  readonly conversationListNextCursor = signal<string | null>(null);
  readonly hasMoreConversationList = signal(false);
  readonly lastError = signal<unknown | null>(null);
  readonly openCount = signal(0);

  private unsubscribe: (() => void) | null = null;
  private subscribedAgentGroupId: string | null = null;
  private transport: ConversationCoreTransport | null = null;
  private migrationRetryTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private mutationSequence = 0;
  private latestConversationMutationSequenceById = new Map<string, number>();
  private latestConversationUpdatedAtById = new Map<string, number>();
  private defaultConversationLimit = 50;
  private defaultMessageLimit = 200;

  constructor(
    transport?: ConversationCoreTransport,
    defaultConversationLimit?: number,
    defaultMessageLimit?: number
  ) {
    if (transport) {
      this.configure(transport, defaultConversationLimit, defaultMessageLimit);
    }
  }

  configure(
    transport: ConversationCoreTransport,
    defaultConversationLimit = 50,
    defaultMessageLimit = 200
  ) {
    this.stop();
    this.transport = transport;
    this.defaultConversationLimit = defaultConversationLimit;
    this.defaultMessageLimit = defaultMessageLimit;
  }

  private requireTransport() {
    if (!this.transport) {
      throw new Error('ConversationLiveDataset transport is not configured.');
    }
    return this.transport;
  }

  private scheduleMigrationRetry(
    key: string,
    error: unknown,
    retry: () => void
  ) {
    const delay = agentDBRetryDelayMs(error);
    if (delay === null || this.migrationRetryTimers.has(key)) return;
    const timer = setTimeout(() => {
      this.migrationRetryTimers.delete(key);
      retry();
    }, delay);
    this.migrationRetryTimers.set(key, timer);
  }

  setAgentGroup(agentGroupId: string | null | undefined) {
    const normalizedAgentGroupId = normalizeText(agentGroupId);
    if (this.agentGroupId.peek() === normalizedAgentGroupId) return false;
    this.stop();
    this.agentGroupId.value = normalizedAgentGroupId;
    this.reset();
    return true;
  }

  upsertConversation(
    incoming: WebAgentConversationInfo | WebConversationListItem,
    options: ConversationUpsertOptions = {}
  ) {
    const mutationSource = options.mutationSource ?? 'snapshot';
    const conversationInfo = isConversationInfo(incoming)
      ? incoming
      : this.infoFromListItem(incoming);
    const conversationId = normalizeText(conversationInfo.conversationId);
    if (!conversationId) return;
    if (
      options.restoreDeleted === false &&
      this.deletedConversationIds.value.has(conversationId)
    ) {
      return;
    }

    const existing = this.conversationInfoById.value.get(conversationId);
    if (
      mutationSource === 'snapshot' &&
      existing &&
      this.isStaleConversationSnapshot(
        conversationId,
        conversationInfo,
        existing,
        options.snapshotStartSequence
      )
    ) {
      this.mergeSnapshotOnlyFields(conversationId, existing, conversationInfo);
      return;
    }

    const incomingParticipants = conversationInfo.participants ?? [];
    const mergedConversation: WebAgentConversationInfo = {
      ...conversationInfo,
      agentGroupId:
        normalizeText(conversationInfo.agentGroupId) ??
        existing?.agentGroupId ??
        null,
      participants:
        incomingParticipants.length > 0
          ? incomingParticipants
          : (existing?.participants ?? []),
    };

    const next = new Map(this.conversationInfoById.value);
    next.set(conversationId, mergedConversation);
    this.conversationInfoById.value = next;
    this.recordConversationWrite(
      conversationId,
      mergedConversation,
      mutationSource
    );

    if (this.deletedConversationIds.value.has(conversationId)) {
      const deleted = new Set(this.deletedConversationIds.value);
      deleted.delete(conversationId);
      this.deletedConversationIds.value = deleted;
    }

    this.syncConversationMembership(mergedConversation);
  }

  setMessages(
    conversationId: string,
    messages: Array<WebAgentConversationMessage | ConversationMessage>
  ) {
    const normalizedConversationId = normalizeText(conversationId);
    if (!normalizedConversationId) return;
    const next = new Map(this.messagesByConversationId.value);
    next.set(normalizedConversationId, messages);
    this.messagesByConversationId.value = next;
    this.removeStaleDraftsForMessages(normalizedConversationId, messages);
  }

  async refreshConversationList(options: {
    limit?: number;
    cursor?: string | null;
    restartStream?: boolean;
    getSelectedConversationId: () => string | null;
    getSelectedListConversationId?: () => string | null;
    onStreamOpen?: (
      selectedConversationId: string | null,
      info: StreamOpenInfo
    ) => void;
  }) {
    const agentGroupId = this.agentGroupId.value;
    if (!agentGroupId) return;

    const transport = this.requireTransport();
    const limit = options.limit ?? this.defaultConversationLimit;
    const snapshotStartSequence = this.snapshotStartSequence();
    this.conversationListRefreshing.value = true;
    this.lastError.value = null;

    try {
      const page = await transport.listConversationPage({
        agentGroupId,
        limit,
        cursor: options.cursor,
      });
      for (const conversation of page.data) {
        this.upsertConversation(conversation, {
          restoreDeleted: false,
          mutationSource: 'snapshot',
          snapshotStartSequence,
        });
      }
      this.setConversationListPage(page.data, {
        cursor: options.cursor,
        snapshotStartSequence,
        preserveConversationIds: [
          options.getSelectedConversationId(),
          options.getSelectedListConversationId?.(),
        ],
      });
      this.conversationListNextCursor.value = page.nextCursor ?? null;
      this.hasMoreConversationList.value = page.hasMore;
      this.conversationListHydrated.value = true;
      if (options.restartStream ?? true) {
        this.start({
          getSelectedConversationId: options.getSelectedConversationId,
          getSelectedListConversationId: options.getSelectedListConversationId,
          onStreamOpen: options.onStreamOpen,
        });
      }
    } catch (error) {
      this.lastError.value = error;
      this.scheduleMigrationRetry('conversation-list', error, () => {
        void this.refreshConversationList(options).catch(retryError => {
          this.lastError.value = retryError;
        });
      });
      throw error;
    } finally {
      this.conversationListRefreshing.value = false;
    }
  }

  async getConversation(conversationId: string) {
    const agentGroupId = this.agentGroupId.value;
    const normalizedConversationId = normalizeText(conversationId);
    if (!agentGroupId || !normalizedConversationId) return null;

    const transport = this.requireTransport();
    const snapshotStartSequence = this.snapshotStartSequence();
    const conversation = await transport.getConversation(
      normalizedConversationId,
      { agentGroupId }
    );
    this.upsertConversation(conversation, {
      mutationSource: 'snapshot',
      snapshotStartSequence,
    });
    return conversation;
  }

  async loadMessageSnapshot(conversationId: string) {
    const agentGroupId = this.agentGroupId.value;
    const normalizedConversationId = normalizeText(conversationId);
    if (!agentGroupId || !normalizedConversationId) return [];

    const transport = this.requireTransport();
    this.lastError.value = null;

    try {
      const messages = await transport.listMessages(normalizedConversationId, {
        agentGroupId,
        limit: this.defaultMessageLimit,
      });
      this.setMessages(normalizedConversationId, messages);
      return messages;
    } catch (error) {
      this.lastError.value = error;
      this.scheduleMigrationRetry(
        `messages:${normalizedConversationId}`,
        error,
        () => {
          void this.loadMessageSnapshot(normalizedConversationId).catch(
            retryError => {
              this.lastError.value = retryError;
            }
          );
        }
      );
      throw error;
    }
  }

  start(options: {
    getSelectedConversationId: () => string | null;
    getSelectedListConversationId?: () => string | null;
    onStreamOpen?: (
      selectedConversationId: string | null,
      info: StreamOpenInfo
    ) => void;
  }) {
    const agentGroupId = this.agentGroupId.value;
    if (!agentGroupId) {
      this.stop();
      return;
    }
    if (this.subscribedAgentGroupId === agentGroupId) return;

    const transport = this.requireTransport();
    this.stop();
    this.subscribedAgentGroupId = agentGroupId;
    this.unsubscribe = transport.subscribeConversationEvents({
      agentGroupId,
      onOpen: () => {
        const nextOpenCount = this.openCount.value + 1;
        this.openCount.value = nextOpenCount;
        const isReconnect = nextOpenCount > 1;
        const shouldRefresh =
          isReconnect ||
          (!this.conversationListHydrated.value &&
            !this.conversationListRefreshing.value);
        if (shouldRefresh) {
          void this.refreshConversationList({
            restartStream: false,
            getSelectedConversationId: options.getSelectedConversationId,
            getSelectedListConversationId:
              options.getSelectedListConversationId,
          }).catch(error => {
            this.lastError.value = error;
          });
        }
        const selectedConversationId = options.getSelectedConversationId();
        options.onStreamOpen?.(selectedConversationId, { isReconnect });
      },
      onEvent: event => this.applyEvent(event),
    });
  }

  stop() {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.subscribedAgentGroupId = null;
  }

  applyEvent(event: WebAgentConversationEvent) {
    const conversationId = normalizeText(event.conversationId);
    if (!conversationId) return;

    if (event.type === 'conversation_delete') {
      this.markDeleted(conversationId, 'live');
      return;
    }

    if (
      (event.type === 'conversation_upsert' ||
        event.type === 'message_created' ||
        event.type === 'message_updated') &&
      event.conversation
    ) {
      this.upsertConversation(event.conversation, {
        mutationSource: 'live',
      });
    }

    if (event.type === 'message_created' || event.type === 'message_updated') {
      if (event.message) {
        if (event.type === 'message_updated') {
          this.replaceExistingMessage(conversationId, event.message);
        } else {
          this.appendCreatedMessage(conversationId, event.message);
        }
      }
      return;
    }

    if (
      event.type === 'message_draft_started' ||
      event.type === 'message_draft_delta' ||
      event.type === 'message_draft_completed' ||
      event.type === 'message_draft_cancelled'
    ) {
      this.applyDraft(event.draft);
    }
  }

  deleteConversation(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    if (!normalizedConversationId) return;
    this.markDeleted(normalizedConversationId, 'local');
  }

  conversationInfoFor(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    return normalizedConversationId
      ? (this.conversationInfoById.value.get(normalizedConversationId) ?? null)
      : null;
  }

  isDeleted(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    return normalizedConversationId
      ? this.deletedConversationIds.value.has(normalizedConversationId)
      : false;
  }

  messagesFor(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    return normalizedConversationId
      ? (this.messagesByConversationId.value.get(normalizedConversationId) ??
          [])
      : [];
  }

  messageSnapshotFor(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    return normalizedConversationId
      ? (this.messagesByConversationId.value.get(normalizedConversationId) ??
          null)
      : null;
  }

  createdMessagesFor(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    return normalizedConversationId
      ? (this.createdMessagesByConversationId.value.get(
          normalizedConversationId
        ) ?? [])
      : [];
  }

  draftsFor(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    return normalizedConversationId
      ? (this.draftsByConversationId.value.get(normalizedConversationId) ?? [])
      : [];
  }

  reset() {
    for (const timer of this.migrationRetryTimers.values()) {
      clearTimeout(timer);
    }
    this.migrationRetryTimers.clear();
    this.conversationInfoById.value = new Map();
    this.listedConversationIds.value = [];
    this.deletedConversationIds.value = new Set();
    this.messagesByConversationId.value = new Map();
    this.createdMessagesByConversationId.value = new Map();
    this.draftsByConversationId.value = new Map();
    this.conversationListRefreshing.value = false;
    this.conversationListHydrated.value = false;
    this.conversationListNextCursor.value = null;
    this.hasMoreConversationList.value = false;
    this.lastError.value = null;
    this.openCount.value = 0;
    this.mutationSequence = 0;
    this.latestConversationMutationSequenceById.clear();
    this.latestConversationUpdatedAtById.clear();
  }

  private markDeleted(
    conversationId: string,
    mutationSource: Exclude<ConversationMutationSource, 'snapshot'>
  ) {
    const next = new Set(this.deletedConversationIds.value);
    next.add(conversationId);
    this.deletedConversationIds.value = next;
    this.recordConversationMutation(conversationId, mutationSource);
    this.listedConversationIds.value = this.listedConversationIds.value.filter(
      id => id !== conversationId
    );
  }

  private setConversationListPage(
    conversations: WebAgentConversationInfo[],
    options: {
      cursor?: string | null;
      snapshotStartSequence?: number;
      preserveConversationIds?: Array<string | null | undefined>;
    } = {}
  ) {
    const pageListIds = uniqueNormalizedIds(
      conversations.flatMap(conversation => {
        const conversationId = normalizeText(conversation.conversationId);
        const stored = conversationId
          ? this.conversationInfoById.value.get(conversationId)
          : null;
        return stored && isListedConversationInfo(stored) ? [conversationId] : [];
      })
    );
    const preservedLiveListIds = this.conversationIdsChangedAfterSnapshot(
      options.snapshotStartSequence
    ).filter(conversationId => {
      const stored = this.conversationInfoById.value.get(conversationId);
      return (
        stored &&
        isListedConversationInfo(stored) &&
        !this.deletedConversationIds.value.has(conversationId)
      );
    });
    const preservedListIds = uniqueNormalizedIds(
      (options.preserveConversationIds ?? []).flatMap(conversationId => {
        const normalizedConversationId = normalizeText(conversationId);
        const stored = normalizedConversationId
          ? this.conversationInfoById.value.get(normalizedConversationId)
          : null;
        return stored &&
          isListedConversationInfo(stored) &&
          !this.deletedConversationIds.value.has(stored.conversationId)
          ? [stored.conversationId]
          : [];
      })
    );
    this.listedConversationIds.value = options.cursor
      ? uniqueNormalizedIds([...this.listedConversationIds.value, ...pageListIds])
      : uniqueNormalizedIds([
          ...preservedLiveListIds,
          ...pageListIds,
          ...preservedListIds,
        ]);
  }

  private snapshotStartSequence() {
    return this.mutationSequence;
  }

  private conversationUpdatedAt(conversation: WebAgentConversationInfo) {
    return Number(conversation.updatedAt || conversation.createdAt || 0) || 0;
  }

  private isStaleConversationSnapshot(
    conversationId: string,
    incoming: WebAgentConversationInfo,
    existing: WebAgentConversationInfo,
    snapshotStartSequence: number | undefined
  ) {
    const incomingUpdatedAt = this.conversationUpdatedAt(incoming);
    const existingUpdatedAt =
      this.latestConversationUpdatedAtById.get(conversationId) ??
      this.conversationUpdatedAt(existing);
    if (incomingUpdatedAt < existingUpdatedAt) return true;
    if (snapshotStartSequence === undefined) return false;
    return (
      this.conversationChangedAfterSnapshot(
        conversationId,
        snapshotStartSequence
      ) && incomingUpdatedAt <= existingUpdatedAt
    );
  }

  private mergeSnapshotOnlyFields(
    conversationId: string,
    existing: WebAgentConversationInfo,
    incoming: WebAgentConversationInfo
  ) {
    const incomingParticipants = incoming.participants ?? [];
    const existingParticipants = existing.participants ?? [];
    const nextConversation: WebAgentConversationInfo = {
      ...existing,
      agentGroupId:
        normalizeText(existing.agentGroupId) ??
        normalizeText(incoming.agentGroupId) ??
        null,
      participants:
        existingParticipants.length > 0 || incomingParticipants.length === 0
          ? existingParticipants
          : incomingParticipants,
    };
    if (
      nextConversation.agentGroupId === existing.agentGroupId &&
      nextConversation.participants === existingParticipants
    ) {
      return;
    }
    const next = new Map(this.conversationInfoById.value);
    next.set(conversationId, nextConversation);
    this.conversationInfoById.value = next;
    this.syncConversationMembership(nextConversation);
  }

  private recordConversationWrite(
    conversationId: string,
    conversation: WebAgentConversationInfo,
    mutationSource: ConversationMutationSource
  ) {
    const currentUpdatedAt =
      this.latestConversationUpdatedAtById.get(conversationId) ?? 0;
    this.latestConversationUpdatedAtById.set(
      conversationId,
      Math.max(currentUpdatedAt, this.conversationUpdatedAt(conversation))
    );
    this.recordConversationMutation(conversationId, mutationSource);
  }

  private recordConversationMutation(
    conversationId: string,
    mutationSource: ConversationMutationSource
  ) {
    if (mutationSource === 'snapshot') return;
    this.mutationSequence += 1;
    this.latestConversationMutationSequenceById.set(
      conversationId,
      this.mutationSequence
    );
  }

  private conversationChangedAfterSnapshot(
    conversationId: string,
    snapshotStartSequence: number
  ) {
    return (
      (this.latestConversationMutationSequenceById.get(conversationId) ?? 0) >
      snapshotStartSequence
    );
  }

  private conversationIdsChangedAfterSnapshot(
    snapshotStartSequence: number | undefined
  ) {
    if (snapshotStartSequence === undefined) return [];
    return [...this.latestConversationMutationSequenceById.entries()]
      .filter(([, sequence]) => sequence > snapshotStartSequence)
      .map(([conversationId]) => conversationId);
  }

  private syncConversationMembership(conversation: WebAgentConversationInfo) {
    const conversationId = normalizeText(conversation.conversationId);
    if (!conversationId) return;

    if (isListedConversationInfo(conversation)) {
      if (
        !this.listedConversationIds.value.includes(conversationId) &&
        !this.deletedConversationIds.value.has(conversationId)
      ) {
        this.listedConversationIds.value = [
          ...this.listedConversationIds.value,
          conversationId,
        ];
      }
      return;
    }

    this.listedConversationIds.value = this.listedConversationIds.value.filter(
      id => id !== conversationId
    );
  }

  appendCreatedMessage(
    conversationId: string,
    message: WebAgentConversationMessage
  ) {
    const next = new Map(this.createdMessagesByConversationId.value);
    next.set(
      conversationId,
      appendMessage(next.get(conversationId) ?? [], message)
    );
    this.createdMessagesByConversationId.value = next;
    if (normalizeText(message.kind) === 'message') {
      this.removeLatestDraftForParticipant(
        conversationId,
        message.participantId
      );
    }
  }

  private removeLatestDraftForParticipant(
    conversationId: string,
    participantId: string | null | undefined
  ) {
    const drafts = this.draftsByConversationId.value.get(conversationId);
    if (!drafts?.length) return;

    const nextDrafts = removeLatestDraftForParticipant(drafts, participantId);
    if (nextDrafts === drafts) return;

    const next = new Map(this.draftsByConversationId.value);
    if (nextDrafts.length === 0) {
      next.delete(conversationId);
    } else {
      next.set(conversationId, nextDrafts);
    }
    this.draftsByConversationId.value = next;
  }

  private removeStaleDraftsForMessages(
    conversationId: string,
    messages: Array<WebAgentConversationMessage | ConversationMessage>
  ) {
    const drafts = this.draftsByConversationId.value.get(conversationId);
    if (!drafts?.length) return;

    const nextDrafts = removeStaleDraftsForMessages(drafts, messages);
    if (nextDrafts === drafts) return;

    const next = new Map(this.draftsByConversationId.value);
    if (nextDrafts.length === 0) {
      next.delete(conversationId);
    } else {
      next.set(conversationId, nextDrafts);
    }
    this.draftsByConversationId.value = next;
  }

  replaceExistingMessage(
    conversationId: string,
    message: WebAgentConversationMessage
  ) {
    const messageId = messageIdOf(message);
    if (!messageId) return;

    const snapshots = new Map(this.messagesByConversationId.value);
    const snapshot = snapshots.get(conversationId) ?? [];
    const snapshotIndex = snapshot.findIndex(
      current => messageIdOf(current) === messageId
    );
    if (snapshotIndex !== -1) {
      const nextSnapshot = [...snapshot];
      nextSnapshot[snapshotIndex] = message;
      snapshots.set(conversationId, nextSnapshot);
      this.messagesByConversationId.value = snapshots;
    }

    const live = this.createdMessagesByConversationId.value.get(conversationId);
    const liveIndex =
      live?.findIndex(current => messageIdOf(current) === messageId) ?? -1;
    if (live && liveIndex !== -1) {
      const nextLiveMessages = [...live];
      nextLiveMessages[liveIndex] = message;
      const nextLive = new Map(this.createdMessagesByConversationId.value);
      nextLive.set(conversationId, nextLiveMessages);
      this.createdMessagesByConversationId.value = nextLive;
    }
  }

  applyDraft(draft: WebAgentConversationMessageDraft) {
    const conversationId = normalizeText(draft.conversationId);
    if (!conversationId) return;
    const next = new Map(this.draftsByConversationId.value);
    next.set(
      conversationId,
      upsertDraft(next.get(conversationId) ?? [], draft)
    );
    this.draftsByConversationId.value = next;
  }

  private infoFromListItem(item: WebConversationListItem) {
    const existing = this.conversationInfoById.value.get(item.conversationId);
    return {
      conversationId: item.conversationId,
      agentGroupId: item.agentGroupId,
      kind:
        normalizeText(item.kind) ??
        normalizeText(existing?.kind) ??
        'user_chat',
      title: item.title,
      status: normalizeText(existing?.status) ?? 'active',
      createdByAgentId:
        normalizeText(item.createdByAgentId) ??
        existing?.createdByAgentId ??
        '',
      createdBySessionId:
        normalizeText(item.createdBySessionId) ??
        existing?.createdBySessionId ??
        '',
      participants: item.participants,
      messageCount: item.messageCount,
      targetMessageCount: item.targetMessageCount,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt || item.createdAt,
    };
  }
}
